import type { AppBase } from 'playcanvas';

// @engine/core — SceneRegistry (REBUILD_PLAN Step 7a / D-C).
// A lightweight registry of named scene-builder functions. The registry holds NO lazy `import()` calls —
// laziness is the game's closure (D-C). The engine supplies the lookup ABI; the caller supplies the factories.

/** A scene builder: receives the fully-initialised `AppBase` and constructs the scene synchronously or async. */
export type SceneBuilder = (app: AppBase) => void | Promise<void>;

/**
 * A registry of named scene builders with a single configured default.
 *
 * Usage:
 * ```ts
 * const registry = new SceneRegistry('world');
 * registry.register('world', (app) => buildWorldScene(app));
 * registry.register('fixture', (app) => buildFixtureScene(app));
 * const builder = registry.resolve(location.search);
 * await builder(app);
 * ```
 */
export class SceneRegistry {
  readonly #defaultScene: string;
  readonly #builders = new Map<string, SceneBuilder>();

  constructor(defaultScene: string) {
    this.#defaultScene = defaultScene;
  }

  /**
   * Register a named scene builder. Throws if the name has already been registered (duplicate-registration
   * is always a programming error — a scene name must be stable, not overridable at runtime).
   */
  register(name: string, builder: SceneBuilder): void {
    if (this.#builders.has(name)) {
      throw new Error(
        `SceneRegistry: duplicate scene name "${name}" — each scene must be registered exactly once.`,
      );
    }
    this.#builders.set(name, builder);
  }

  /**
   * Resolve the builder for the scene named by `?scene=<name>` in `search`.
   * - Unknown `?scene=` values fall back to the configured default (matches today's if/else else-branch).
   * - Absent `?scene=` falls back to the configured default.
   * - All other query parameters (e.g. `?pin`, `?nolod`, `?server`, `?gfx`) are passed through untouched —
   *   they are read by their own owners, not consumed here.
   * - Throws if the default scene has not been registered (a programming error caught at startup).
   */
  resolve(search: string | URLSearchParams): SceneBuilder {
    const params = typeof search === 'string' ? new URLSearchParams(search) : search;
    const name = params.get('scene') ?? this.#defaultScene;
    const builder = this.#builders.get(name);
    if (builder !== undefined) return builder;
    // Unknown scene name → fall back to default (matches today's if/else else-branch behaviour).
    const fallback = this.#builders.get(this.#defaultScene);
    if (fallback === undefined) {
      throw new Error(
        `SceneRegistry: default scene "${this.#defaultScene}" has not been registered. ` +
          `Call registry.register("${this.#defaultScene}", builder) before calling resolve().`,
      );
    }
    return fallback;
  }
}
